import React, { useCallback, useState, useEffect } from 'react';
import { useLocalizedText } from '../../../hooks/localizedText';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import {
  AppLayout,
  BreadcrumbGroup,
  Flashbar,
  Header,
  ContentLayout,
  Spinner,
} from '@amzn/awsui-components-react';
import { AttendanceWizard, FIRST_PAGE } from '../wizard/attendanceWizard';
import { AttendanceModal } from '../leaveModal';
import { BulkUploadModal } from '../bulkUpload/bulkUploadModal';

import { AttendeeTable } from '../csvAttendance/attendeeTable';
import { EditEventInfo } from '../editEvent/editEventInfo';
import { AdditionalInfo } from '../editEvent/additionalInfo';
import { PostEventInfo } from '../editEvent/postEventInfo';
import { SpaceBetween } from '@amzn/awsui-components-react';
import { CoordinatorAttendeeTable } from './coordinatorAttendeeTable';
import { urlWithPersistantParams } from '../../SpaLink';
import { publishKatalMetric } from '../../katalAnalytics';
import { useWizardStrings } from '../../../hooks/localization/wizardStrings';
import { useErrorMessages } from '../../../hooks/localization/errorMessages';
import { useUrlPage } from '../../../hooks/attendance/urlPage';

import { useShiftAttendance } from '../../../hooks/attendance/attendanceMutation';

import { useAttendeeSessionStorage } from '../../../hooks/attendance/attendeeSessionStorage';
import { useParticipantSessionStorage } from '../../../hooks/attendance/participantSessionStorage';
import { useParticipants } from '../../../hooks/attendance/useParticipants';
import { useUser } from '../../../hooks/user';
import { getValueFromSearchParam } from '../../../utils/url';
import { shouldBlockAsVCDraft } from '../../../utils/eventUtils';

import { ShiftAttendanceHeader } from './shiftAttendanceHeader';
import { EventAttendanceHeader } from './eventAttendanceHeader';

import { useEditableEventInfo, EditableInfoField } from '../../../hooks/events/editableEventInfo';
import { AttendanceStatus, CreateAttendanceRequest, Source } from '@amzn/red-velvet-api';
import { featureIsEnabled, useFeatures } from '../../../hooks/features';
import { EVENT_CREATION, NAVIGATION_REDESIGN } from '../../../utils/constants';
import { canManageGivehubEvent } from '../../../utils/shiftUtils';
import { useBulkParticipantSessionStorage } from '../../../hooks/attendance/bulkParticipantSessionStorage';
import { getGuardedShiftLength } from '../../../hooks/shifts/shiftTime';
import { AppOutletContext } from '../../../App';

function EventDetailValidationSummary({
  errors,
}: {
  errors: { [s in EditableInfoField]?: string };
}) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useLocalizedText(namespaces);

  const errorKeys = (Object.keys(errors) as Array<EditableInfoField>).filter((key) => errors[key]);

  return (
    <div>
      <span>
        {t('shiftAttendanceWizard.eventDetails.validationErrors', { count: errorKeys.length })}
      </span>
      <ul>
        {errorKeys.map((key) => (
          <li key={key}>{errors[key]}</li>
        ))}
      </ul>
    </div>
  );
}

// Based on Golden Path in https://code.amazon.com/packages/RedVelvetReactWebsite/blobs/mainline/--/src/components/attendance/csvAttendance/csvAttendance.tsx
export function CoordinatorAttendancePage() {
  const namespaces = ['attendance', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const { resolveError } = useErrorMessages();
  const user = useUser();
  const features = useFeatures();

  // Note - these have to be passed down to the step from the parent so that we can validate the changes when a user clicks next step
  // and send the values to the API when the user clicks submit
  const { page, setPage, updateParams: updatePageParams } = useUrlPage(FIRST_PAGE);

  const [searchParams, setSearchParams] = useSearchParams();

  const eventId = getValueFromSearchParam(searchParams, 'eventId', '');
  const shiftId = getValueFromSearchParam(searchParams, 'shiftId', '');

  // Determine which page header to show
  const isEventLevelTracking = eventId && !shiftId;
  const isShiftLevelTracking = eventId && shiftId;
  const isUnlistedEventTracking = !eventId && !shiftId && page !== 0;
  const shouldShowHeader = isEventLevelTracking || isShiftLevelTracking || isUnlistedEventTracking;

  const navigate = useNavigate();
  const [modalVisible, setModalVisible] = useState(false);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [navigateTo, setNavigateTo] = useState('#');

  const { participants, resetParticipants } = useParticipantSessionStorage();
  const { aliases, resetAttendees } = useAttendeeSessionStorage();
  const { bulkParticipants, resetBulkParticipants } = useBulkParticipantSessionStorage();

  const {
    editableInfo,
    defaultsFromApi,
    event,
    shift,
    shifts,
    hasChanges: eventChanges,
    isFetched,
    eventWarnings,
    eventErrors,
    validateFields,
    updateEditableInfoFields,
    saveEventToUrl,
    updateParams: updateEventParams,
  } = useEditableEventInfo();

  // Get attendee data and loading state for auto-open logic
  const attendeeCollection = useParticipants(
    ['attendance', 'translation'],
    'coordinatorAttendeeTable',
    eventId,
    shiftId,
    100,
    shifts
  );
  const { items: attendeeItems, isLoading: attendeesLoading } = attendeeCollection;
  // Keep the explicit GIVEHUB guard: canManageGivehubEvent() returns false for VC events,
  // so without this guard !canManageGivehubEvent(...) would incorrectly skip the step for VC events.
  const shouldSkipEventDetailsStep =
    event.source === Source.GIVEHUB && !canManageGivehubEvent(event, user);

  // Listed events with EVENT_CREATION enabled show only the Post-event Information
  // section (no Basic Info, no Community Org, no ELG Sponsors - those are on Edit Event page)
  const isListedEventPostEventInfo =
    featureIsEnabled(EVENT_CREATION, features, searchParams) &&
    event.source === Source.GIVEHUB &&
    canManageGivehubEvent(event, user);

  // Correct the URL page index on initial load:
  // - When Event Details is skipped (shouldSkipEventDetailsStep), offset page by -1
  // - When Event Details exists but required fields are incomplete, reset to page 0
  const isEventLoaded = !eventId || (isFetched && event.eventId === eventId);
  const hasIncompleteEventDetails =
    eventId &&
    !shouldSkipEventDetailsStep &&
    !isListedEventPostEventInfo &&
    !(event.primaryActivity && event.focusArea && event.usedAciGrant != null);
  const needsPageOffset = shouldSkipEventDetailsStep && page > 0;
  const needsPageReset = (hasIncompleteEventDetails || isListedEventPostEventInfo) && page > 0;
  const needsCorrection = needsPageOffset || needsPageReset;
  const [pageAdjusted, setPageAdjusted] = useState(false);

  // When a coordinator navigates between events (e.g. back button, then clicking a different
  // event), the page correction must run again for the new event. Without this reset, the
  // wizard would open on the wrong step because it thinks the page was already corrected.
  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPageAdjusted(false);
  }, [eventId, shiftId]);

  useEffect(() => {
    if (!isEventLoaded || pageAdjusted) return;
    if (needsPageOffset) {
      setSearchParams((params) => updatePageParams(params, page - 1), { replace: true });
    } else if (needsPageReset) {
      setSearchParams((params) => updatePageParams(params, 0), { replace: true });
    }
    setPageAdjusted(true);
  }, [
    isEventLoaded,
    pageAdjusted,
    needsPageOffset,
    needsPageReset,
    page,
    setSearchParams,
    updatePageParams,
  ]);

  // Only block rendering when a page correction is pending and hasn't been applied yet
  const isWizardReady = isEventLoaded && (!needsCorrection || pageAdjusted);

  // If we have data saved in the url, we want to confirm with the user before navigating away
  const hasChanges =
    eventChanges ||
    Object.keys(participants).length !== 0 ||
    Object.keys(bulkParticipants).length !== 0 ||
    aliases.length !== 0;

  const onNavigateAway = (href: string) => {
    if (hasChanges) {
      setModalVisible(true);
      setNavigateTo(href);
    } else {
      resetAttendees();
      resetParticipants();
      // Preserve features/timezone params so we don't drop the user back into the legacy layout.
      navigate(urlWithPersistantParams(searchParams, href));
    }
  };
  const leave = () => {
    resetAttendees();
    resetParticipants();
    // Preserve features/timezone params so discarding changes doesn't strip them and fall back
    // to the legacy layout (same fix applied to the create/edit event wizards).
    navigate(urlWithPersistantParams(searchParams, navigateTo));
  };

  const wizardStrings = useWizardStrings('shiftAttendanceWizard', namespaces);

  const attendanceMutation = useShiftAttendance(user.userId, 25);

  const eventDetailsSteps = [];
  if (!shouldSkipEventDetailsStep) {
    eventDetailsSteps.push({
      title: t('shiftAttendanceWizard.stepTitles.eventDetails'),
      content: isFetched ? (
        isListedEventPostEventInfo ? (
          <PostEventInfo
            editableInfo={editableInfo}
            updateEditableInfoFields={updateEditableInfoFields}
            eventErrors={eventErrors}
          />
        ) : (
          <SpaceBetween direction="vertical" size="l">
            <EditEventInfo
              editableInfo={editableInfo}
              updateEditableInfoFields={updateEditableInfoFields}
              eventWarnings={eventWarnings}
              eventErrors={eventErrors}
            />
            <AdditionalInfo
              editableInfo={editableInfo}
              updateEditableInfoFields={updateEditableInfoFields}
              eventWarnings={eventWarnings}
              eventErrors={eventErrors}
            />
          </SpaceBetween>
        )
      ) : (
        <SpaceBetween direction="vertical" size="l">
          <Spinner />
          <span>{t('fullEvent.loadingEvent')}</span>
        </SpaceBetween>
      ),
      errorText:
        (Object.keys(eventErrors) as Array<EditableInfoField>).filter((key) => eventErrors[key])
          .length > 0 ? (
          <EventDetailValidationSummary errors={eventErrors} />
        ) : null,
    });
  }

  // Auto-open modal when on Track Attendance step with 0 attendees (after API loads)
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  // On mounting the Track Attendance wizard, clean up any previous bulk-participants session storage
  // TODO: finalize whether to clean bulk-participants on page refresh after discuss with Gil SIM: cce-6071
  useEffect(() => {
    resetBulkParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isBlocked = !!eventId && shouldBlockAsVCDraft(event);
  const isDeleted = !!event?.vcDeleted;
  useEffect(() => {
    if (isBlocked) {
      publishKatalMetric('createEvent_draftAlertTrackAttendance', event.eventId);
    }
  }, [isBlocked, event.eventId]);

  useEffect(() => {
    if (
      !isBlocked &&
      page === (shouldSkipEventDetailsStep ? 0 : 1) &&
      isFetched &&
      !attendeesLoading &&
      attendeeItems.length === 0 &&
      !hasAutoOpened
    ) {
      const metricValue = isEventLevelTracking
        ? 'autoOpenEventLevelTracking'
        : 'autoOpenSingleShiftTracking';
      publishKatalMetric('attendance_bulkUploadModalOpened', metricValue);
      setBulkUploadModalVisible(true);
      setHasAutoOpened(true);
    }
  }, [
    isBlocked,
    page,
    shouldSkipEventDetailsStep,
    isEventLevelTracking,
    isFetched,
    attendeesLoading,
    attendeeItems.length,
    hasAutoOpened,
  ]);

  const steps = [
    ...eventDetailsSteps,
    {
      title: t('shiftAttendanceWizard.stepTitles.trackAttendance'),
      content: (
        <AttendeeTable
          shifts={shifts}
          vcCreated={event.vcCreated}
          onAddAttendees={() => {
            publishKatalMetric(
              'attendance_bulkUploadModalOpened',
              isEventLevelTracking ? 'multiShiftTracking' : 'singleShiftTracking'
            );
            setBulkUploadModalVisible(true);
          }}
        />
      ),
      description: t('shiftAttendanceWizard.addUsersInstructions'),
    },
    {
      title: t('shiftAttendanceWizard.stepTitles.trackTime'),
      content: <CoordinatorAttendeeTable shifts={shifts} vcCreated={event.vcCreated} />,
      description: t('shiftAttendanceWizard.trackTimeInstructions'),
    },
    {
      title: t('shiftAttendanceWizard.stepTitles.submit'),
      content: <CoordinatorAttendeeTable isImmutable shifts={shifts} vcCreated={event.vcCreated} />,
      errorText: attendanceMutation.error ? resolveError(attendanceMutation.error) : null,
    },
  ];

  const isNewNav = featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams);
  const outletContext = useOutletContext<AppOutletContext | null>();

  // Cloudscape unsaved-changes pattern: register a navigation guard so that
  // SiteBreadcrumbs (and any other shared nav component) checks for unsaved
  // changes before navigating away. The guard shows the existing AttendanceModal.
  const navigationGuard = useCallback(
    (href: string) => {
      if (hasChanges) {
        setModalVisible(true);
        setNavigateTo(href);
        return false; // block navigation — modal will handle it
      }
      return true; // allow navigation
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasChanges]
  );

  useEffect(() => {
    if (isNewNav && outletContext) {
      outletContext.setNavigationGuard(navigationGuard);
    }
    return () => {
      if (isNewNav && outletContext) {
        outletContext.setNavigationGuard(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav, navigationGuard]);

  const content = (
    <ContentLayout
      header={
        <div className="page-content-header">
          <Header variant="h1">
            {t(
              isEventLevelTracking
                ? 'pageTitle.attendanceSheetEventLevel'
                : 'pageTitle.attendanceSheet'
            )}
          </Header>
        </div>
      }
    >
      {/* Event and shift HAVE to be passed down through the component chain here, so that they can pick up state changes in the event info form */}
      {shouldShowHeader &&
        (isEventLevelTracking ? (
          <EventAttendanceHeader event={event} isEventFetched={isFetched} />
        ) : (
          <ShiftAttendanceHeader event={event} shift={shift} isFetched={isFetched} />
        ))}
      {isDeleted ? (
        <Flashbar
          items={[
            {
              type: 'error',
              header: t('draftBlock.deletedEvent.header', { ns: 'attendance' }),
              content: t('draftBlock.deletedEvent.content', { ns: 'attendance' }),
            },
          ]}
        />
      ) : isBlocked ? (
        <Flashbar
          items={[
            {
              type: 'warning',
              header: t('draftBlock.warningHeader', { ns: 'attendance' }),
              content: t('draftBlock.warning', { ns: 'attendance' }),
            },
          ]}
        />
      ) : !isWizardReady ? (
        <SpaceBetween direction="vertical" size="l">
          <Spinner />
          <span>{t('fullEvent.loadingEvent')}</span>
        </SpaceBetween>
      ) : (
        <AttendanceWizard
          i18nStrings={{
            ...wizardStrings,
            // Next button should match the title of the next step
            nextButton: t([`shiftAttendanceWizard.nextButton`], {
              title: steps[page + 1] ? steps[page + 1].title : t([`wizard.nextButton`]),
            }),
          }}
          isLoadingNextStep={attendanceMutation.isPending}
          steps={steps}
          onCancel={() => {
            onNavigateAway('/coordinator-activity?tab=past');
          }}
          onNavigate={({ detail }) => {
            if (page === 0 && !shouldSkipEventDetailsStep) {
              const fieldsToValidate = isListedEventPostEventInfo
                ? {
                    registered_community_ambassadors: editableInfo.registered_community_ambassadors,
                    organization_point_of_contact_name:
                      editableInfo.organization_point_of_contact_name,
                    organization_point_of_contact_email:
                      editableInfo.organization_point_of_contact_email,
                    engagement_recap: editableInfo.engagement_recap,
                    photo_upload: editableInfo.photo_upload,
                  }
                : editableInfo;
              if (detail.requestedStepIndex > page && !validateFields(fieldsToValidate)) {
                // Only update the event in the url and DO NOT change pages
                saveEventToUrl();
              } else {
                // Update both the page and event params in the url at the same time
                setSearchParams((params) => {
                  const paramsWithNewPage = updatePageParams(params, detail.requestedStepIndex);
                  return updateEventParams(paramsWithNewPage, editableInfo, defaultsFromApi);
                });
              }
            } else {
              setPage(detail.requestedStepIndex);
            }
          }}
          onSubmit={() => {
            if (isEventLevelTracking && isFetched) {
              // Event-level: group attendance requests by shift and submit separately
              const requestsByShift = new Map<string, CreateAttendanceRequest[]>();

              // Include existing participations from API (attendeeItems)
              // Exclude self-tracked attendance (where alias === trackedBy)
              attendeeItems.forEach((item) => {
                if (!item.shiftId) return;
                // Skip self-tracked attendance - user already tracked their own time
                if (item.alias === item.trackedBy && item.trackedBy !== user.userId) return;
                // Skip attendance that's still in pending status
                if (!item.attendance) return;

                const attendanceStatus = item.attendance || 'ATTENDED';
                const itemShift = shifts.find((s) => s.shiftId === item.shiftId);
                const shiftLength = getGuardedShiftLength(itemShift, event.vcCreated);

                const request: CreateAttendanceRequest = {
                  alias: item.alias,
                  eventId: eventId,
                  shiftId: item.shiftId,
                  minutes:
                    attendanceStatus === AttendanceStatus.ATTENDED
                      ? item.trackedTime || shiftLength
                      : 0,
                  attendanceStatus: attendanceStatus as AttendanceStatus,
                };

                const shiftRequests = requestsByShift.get(item.shiftId) || [];
                shiftRequests.push(request);
                requestsByShift.set(item.shiftId, shiftRequests);
              });

              // Override with any changes from bulkParticipants (user modifications)
              Object.values(bulkParticipants).forEach((data) => {
                // shiftId should always exist for event-level (set during bulk upload validation and shift selection)
                // Guard is for type safety only - should not happen in practice
                if (!data.shiftId) return;

                const attendanceStatus = data.attendance || 'ATTENDED';
                const itemShift = shifts.find((s) => s.shiftId === data.shiftId);
                const shiftLength = getGuardedShiftLength(itemShift, event.vcCreated);

                const request: CreateAttendanceRequest = {
                  alias: data.alias,
                  eventId: eventId,
                  shiftId: data.shiftId,
                  minutes:
                    attendanceStatus === AttendanceStatus.ATTENDED
                      ? data.trackedTime || shiftLength
                      : 0,
                  attendanceStatus: attendanceStatus as AttendanceStatus,
                };

                // Find and replace existing request for this alias-shift combination
                const shiftRequests = requestsByShift.get(data.shiftId) || [];
                const existingIndex = shiftRequests.findIndex((r) => r.alias === data.alias);
                if (existingIndex >= 0) {
                  shiftRequests[existingIndex] = request;
                } else {
                  shiftRequests.push(request);
                }
                requestsByShift.set(data.shiftId, shiftRequests);
              });

              // Submit attendance for each shift separately
              const promises = Array.from(requestsByShift.entries())
                .map(([shiftId, requests]) => {
                  const shiftData = shifts.find((s) => s.shiftId === shiftId);
                  if (!shiftData) return null; // Skip if shift not found
                  return attendanceMutation.mutateAsync({
                    event,
                    shift: shiftData,
                    attendanceInputs: { attendanceRequests: requests },
                  });
                })
                .filter((p): p is ReturnType<typeof attendanceMutation.mutateAsync> => p !== null);

              Promise.all(promises).then(() => {
                resetAttendees();
                resetParticipants();
                resetBulkParticipants();
                navigate(
                  urlWithPersistantParams(
                    searchParams,
                    '/coordinator-activity?tab=past&status=success'
                  )
                );
              });
            } else {
              // Single-shift: use simple alias keys from all sources
              const attendanceRequests: CreateAttendanceRequest[] = [];

              const allAliases = Array.from(
                new Set([
                  ...Object.keys(participants),
                  ...Object.keys(bulkParticipants),
                  ...aliases,
                ])
              );

              allAliases.forEach((alias) => {
                const attendanceData = participants[alias];
                const bulkData = bulkParticipants[alias];
                const attendanceAddedByDefault = aliases.includes(alias)
                  ? 'ATTENDED'
                  : 'DID_NOT_ATTEND';
                const attendanceStatus =
                  attendanceData?.attendance || bulkData?.attendance || attendanceAddedByDefault;

                attendanceRequests.push({
                  alias,
                  eventId: eventId,
                  shiftId: shiftId,
                  minutes:
                    attendanceStatus === AttendanceStatus.ATTENDED
                      ? attendanceData?.trackedTime || shift.shiftLength
                      : 0,
                  attendanceStatus: attendanceStatus as AttendanceStatus,
                });
              });

              attendanceMutation.mutate(
                {
                  event,
                  shift,
                  attendanceInputs: { attendanceRequests },
                },
                {
                  onSuccess: () => {
                    resetAttendees();
                    resetParticipants();
                    resetBulkParticipants();
                    navigate(
                      urlWithPersistantParams(
                        searchParams,
                        '/coordinator-activity?tab=past&status=success'
                      )
                    );
                  },
                }
              );
            }
          }}
        />
      )}
      <AttendanceModal
        hasChanges={hasChanges}
        onConfirm={leave}
        visible={modalVisible && !isDeleted}
        onDismiss={() => setModalVisible(false)}
      />
      {!isBlocked && (shouldSkipEventDetailsStep || page !== 0) && (
        <BulkUploadModal
          visible={bulkUploadModalVisible && !isDeleted}
          onDismiss={() => setBulkUploadModalVisible(false)}
          event={event}
          shifts={shifts}
          shiftId={shiftId || undefined}
        />
      )}
    </ContentLayout>
  );

  // New navigation: return content only, shell handled by root AppLayout
  if (isNewNav) {
    return content;
  }

  // Legacy: keep existing AppLayout wrapper
  return (
    <AppLayout
      footerSelector=".footer"
      navigationHide={true}
      toolsHide={true}
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: t('breadcrumbs.home', { ns: 'translation' }), href: '/' },
            { text: t('breadcrumbs.coordinatorActivity'), href: '/coordinator-activity' },
            { text: t('breadcrumbs.attendanceSheet'), href: '#' },
          ]}
          onFollow={(event) => {
            onNavigateAway(event.detail.href);
            event.preventDefault();
          }}
        />
      }
      content={content}
      contentType="wizard"
    />
  );
}
